import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Stack,
} from "@mui/material";
import { keyframes } from "@mui/system";
import LoadingSpinner from "../components/common/LoadingSpinner";
import UserAvatar from "../components/common/UserAvatar";
import {
  EmojiEvents as TrophyIcon,
  Assignment as ChoreIcon,
  SportsBasketball as AthleticIcon,
  AttachMoney as FinancialIcon,
  Extension as CustomIcon,
  Schedule as PendingIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as CompletedIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import {
  Activity,
  ActivityCategory,
  ActivityStatus,
  User,
  UserRole,
  Family,
  RewardType,
} from "../types";
import {
  getActivitiesByFamily,
  getActivitiesByUser,
} from "../services/activities";
import { getFamilyMembers, getFamily } from "../services/family";

interface LeaderboardEntry {
  userId: string;
  name: string;
  points: number;
  money: number; // USD earned
  completedThisWeek: number;
  totalActivities: number;
  avatar?: string;
  role: string;
}

// Define pulse animation for overdue activities
const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [familyData, setFamilyData] = useState<Family | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is admin (parent, teacher, coach)
  const isAdmin =
    user &&
    [UserRole.PARENT, UserRole.TEACHER, UserRole.COACH].includes(user.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user) {
          setLoading(true);

          // Get activities, family members, and family settings
          const [activitiesData, membersData, familyInfo] = await Promise.all([
            isAdmin
              ? getActivitiesByFamily(user.familyId)
              : getActivitiesByUser(user.userId),
            getFamilyMembers(user.familyId),
            getFamily(user.familyId).catch(() => null), // Handle gracefully if not available
          ]);

          setActivities(activitiesData);
          setFamilyMembers(
            membersData.filter(
              (member: any) => member.userId && member.status !== "PENDING"
            )
          );
          setFamilyData(familyInfo);
        }
      } catch (err) {
        setError("Failed to load dashboard data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isAdmin]);

  const getCategoryIcon = (category: ActivityCategory) => {
    switch (category) {
      case ActivityCategory.CHORE:
        return <ChoreIcon />;
      case ActivityCategory.ATHLETIC:
        return <AthleticIcon />;
      case ActivityCategory.FINANCIAL:
        return <FinancialIcon />;
      case ActivityCategory.CUSTOM:
        return <CustomIcon />;
      default:
        return <ChoreIcon />;
    }
  };

  const getCategoryColor = (category: ActivityCategory) => {
    switch (category) {
      case ActivityCategory.CHORE:
        return "#FF6B35";
      case ActivityCategory.ATHLETIC:
        return "#2BB3A3";
      case ActivityCategory.FINANCIAL:
        return "#174B67";
      case ActivityCategory.CUSTOM:
        return "#9C27B0";
      default:
        return "#666";
    }
  };

  const getMemberName = (userId: string) => {
    const member = familyMembers.find((m) => m.userId === userId);
    return member ? `${member.firstName} ${member.lastName}` : "Unassigned";
  };

  // Calculate leaderboard data
  const getLeaderboardData = (): LeaderboardEntry[] => {
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const leaderboard: LeaderboardEntry[] = familyMembers.map((member) => {
      const memberActivities = activities.filter(
        (a) => a.userId === member.userId
      );
      const completedActivities = memberActivities.filter((a) =>
        [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(a.status)
      );

      const completedThisWeek = completedActivities.filter(
        (a) => a.completedDate && new Date(a.completedDate) >= startOfWeek
      );

      // Calculate points and money earned
      const pointsEarned = completedActivities.reduce((sum, a) => {
        // For backward compatibility, use points field if rewardType/rewardAmount not available
        if (a.rewardType === RewardType.POINTS) {
          return sum + (a.rewardAmount || a.points);
        } else if (!a.rewardType) {
          // Legacy activities without rewardType
          return sum + a.points;
        }
        return sum;
      }, 0);

      const moneyEarned = completedActivities.reduce((sum, a) => {
        if (a.rewardType === RewardType.MONEY) {
          return sum + (a.rewardAmount || 0);
        }
        return sum;
      }, 0);

      return {
        userId: member.userId,
        name: `${member.firstName} ${member.lastName}`,
        points: pointsEarned,
        money: moneyEarned,
        completedThisWeek: completedThisWeek.length,
        totalActivities: memberActivities.length,
        role: member.role,
      };
    });

    // Sort by total value (points + money converted to points for comparison)
    // For simplicity, let's sort by points first, but we'll display both
    return leaderboard.sort((a, b) => b.points - a.points);
  };

  // Get upcoming activities (next 7 days)
  const getUpcomingActivities = () => {
    const now = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(now.getDate() + 7);

    return activities
      .filter(
        (a) =>
          a.dueDate &&
          new Date(a.dueDate) >= now &&
          new Date(a.dueDate) <= nextWeek &&
          a.status !== ActivityStatus.COMPLETED &&
          a.status !== ActivityStatus.VERIFIED
      )
      .sort(
        (a, b) =>
          new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime()
      )
      .slice(0, 8);
  };

  // Generate weekly progress chart data
  const getWeeklyProgressData = () => {
    const weekStartDay = familyData?.weekStartDay ?? 1; // Default to Monday (1)
    const today = new Date();
    const currentWeekStart = new Date(today);

    // Calculate start of current week based on weekStartDay
    const daysSinceWeekStart = (today.getDay() - weekStartDay + 7) % 7;
    currentWeekStart.setDate(today.getDate() - daysSinceWeekStart);
    currentWeekStart.setHours(0, 0, 0, 0);

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dailyData: any[] = [];

    // First pass: Calculate daily data
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(currentWeekStart);
      currentDay.setDate(currentWeekStart.getDate() + i);

      const dayEnd = new Date(currentDay);
      dayEnd.setHours(23, 59, 59, 999);

      // Calculate points earned on this day (completed activities)
      const dayActivities = activities.filter((activity) => {
        if (!activity.completedDate) return false;
        const completedDate = new Date(activity.completedDate);
        return (
          completedDate >= currentDay &&
          completedDate <= dayEnd &&
          [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(
            activity.status
          )
        );
      });

      const totalPointsForDay = dayActivities.reduce(
        (sum, activity) => sum + activity.points,
        0
      );

      // Calculate upcoming activities for this day
      const upcomingDayActivities = activities.filter((activity) => {
        if (!activity.dueDate) return false;
        const dueDate = new Date(activity.dueDate);
        return (
          dueDate >= currentDay &&
          dueDate <= dayEnd &&
          ![ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(
            activity.status
          )
        );
      });

      const totalUpcomingPointsForDay = upcomingDayActivities.reduce(
        (sum, activity) => sum + activity.points,
        0
      );

      const upcomingActivitiesCount = upcomingDayActivities.length;

      // Calculate points per family member for this day (completed)
      const memberPoints: { [key: string]: number } = {};
      // Calculate upcoming activities per family member for this day
      const memberUpcomingPoints: { [key: string]: number } = {};

      familyMembers.forEach((member) => {
        const memberDayActivities = dayActivities.filter(
          (a) => a.userId === member.userId
        );
        memberPoints[member.firstName] = memberDayActivities.reduce(
          (sum, a) => sum + a.points,
          0
        );

        const memberUpcomingActivities = upcomingDayActivities.filter(
          (a) => a.userId === member.userId
        );
        memberUpcomingPoints[`${member.firstName}_upcoming`] =
          memberUpcomingActivities.reduce((sum, a) => sum + a.points, 0);
      });

      const dayIndex = (weekStartDay + i) % 7;
      const formattedDate = currentDay.toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
      });

      dailyData.push({
        day: dayNames[dayIndex],
        dayWithDate: `${dayNames[dayIndex]}\n${formattedDate}`,
        totalPoints: totalPointsForDay,
        totalUpcomingPoints: totalUpcomingPointsForDay,
        upcomingCount: upcomingActivitiesCount,
        ...memberPoints,
        ...memberUpcomingPoints,
        date: currentDay.toISOString().split("T")[0], // For tooltips
      });
    }

    // Second pass: Convert to cumulative data
    const chartData: any[] = [];
    let cumulativeTotalPoints = 0;
    let cumulativeTotalUpcomingPoints = 0;
    const cumulativeMemberPoints: { [key: string]: number } = {};
    const cumulativeMemberUpcomingPoints: { [key: string]: number } = {};

    // Initialize cumulative member points
    familyMembers.forEach((member) => {
      cumulativeMemberPoints[member.firstName] = 0;
      cumulativeMemberUpcomingPoints[`${member.firstName}_upcoming`] = 0;
    });

    const todayForComparison = new Date();
    todayForComparison.setHours(23, 59, 59, 999); // End of today

    dailyData.forEach((dayData, index) => {
      const dayDate = new Date(dayData.date);
      const isToday =
        dayDate.toDateString() === todayForComparison.toDateString();
      const isPastOrToday = dayDate <= todayForComparison;

      // Add daily points to cumulative totals
      cumulativeTotalPoints += dayData.totalPoints;
      cumulativeTotalUpcomingPoints += dayData.totalUpcomingPoints;

      // Create the chart data point
      const cumulativeData: any = {
        day: dayData.day,
        dayWithDate: dayData.dayWithDate,
        upcomingCount: dayData.upcomingCount,
        date: dayData.date,
      };

      // For family total: use completed up to today, then add upcoming for future
      if (isPastOrToday) {
        cumulativeData.familyTotal = cumulativeTotalPoints;
      } else {
        // For future dates, continue from completed total and add upcoming
        cumulativeData.familyTotal =
          cumulativeTotalPoints + cumulativeTotalUpcomingPoints;
      }

      // For each family member: use completed up to today, then add upcoming for future
      familyMembers.forEach((member) => {
        cumulativeMemberPoints[member.firstName] +=
          dayData[member.firstName] || 0;
        cumulativeMemberUpcomingPoints[`${member.firstName}_upcoming`] +=
          dayData[`${member.firstName}_upcoming`] || 0;

        if (isPastOrToday) {
          // Use cumulative completed points up to today
          cumulativeData[member.firstName] =
            cumulativeMemberPoints[member.firstName];
        } else {
          // For future dates, continue from completed total and add upcoming
          cumulativeData[member.firstName] =
            cumulativeMemberPoints[member.firstName] +
            cumulativeMemberUpcomingPoints[`${member.firstName}_upcoming`];
        }
      });

      chartData.push(cumulativeData);
    });

    return chartData;
  };

  // Get colors for each family member line in the chart
  const getMemberColors = () => {
    const colors = [
      "#2BB3A3",
      "#FF6B35",
      "#174B67",
      "#9C27B0",
      "#FF9800",
      "#4CAF50",
      "#E91E63",
    ];
    const memberColors: { [key: string]: string } = {};

    familyMembers.forEach((member, index) => {
      memberColors[member.firstName] = colors[index % colors.length];
    });

    return memberColors;
  };

  if (loading) {
    return (
      <LoadingSpinner
        variant="branded"
        showLogo={true}
        message="Loading Dashboard..."
        subMessage="Fetching your family's activity data"
      />
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  // Calculate current week boundaries
  const getCurrentWeekActivities = () => {
    const weekStartDay = familyData?.weekStartDay ?? 1; // Default to Monday (1)
    const today = new Date();
    const currentWeekStart = new Date(today);

    // Calculate start of current week based on weekStartDay
    const daysSinceWeekStart = (today.getDay() - weekStartDay + 7) % 7;
    currentWeekStart.setDate(today.getDate() - daysSinceWeekStart);
    currentWeekStart.setHours(0, 0, 0, 0);

    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);

    return userActivities.filter((activity) => {
      const activityDate = new Date(activity.createdAt);
      return activityDate >= currentWeekStart && activityDate <= currentWeekEnd;
    });
  };

  const userActivities = isAdmin
    ? activities
    : activities.filter((a) => a.userId === user?.userId);

  // Current week activities for statistics
  const currentWeekActivities = getCurrentWeekActivities();

  const overdueActivities = userActivities.filter((activity) => {
    if (!activity.dueDate) return false;
    const dueDate = new Date(activity.dueDate);
    const now = new Date();
    return (
      dueDate < now &&
      ![ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(
        activity.status
      )
    );
  });
  const pendingActivities = userActivities.filter(
    (activity) =>
      activity.status === ActivityStatus.PENDING ||
      activity.status === ActivityStatus.IN_PROGRESS
  );

  const recentCompletedActivities = userActivities
    .filter(
      (activity) =>
        activity.status === ActivityStatus.COMPLETED ||
        activity.status === ActivityStatus.VERIFIED
    )
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
    .slice(0, 6);

  // Use current week activities for statistics
  const totalActivities = currentWeekActivities.length;
  const completedActivities = currentWeekActivities.filter((a) =>
    [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(a.status)
  ).length;
  const inProgressActivities = currentWeekActivities.filter(
    (a) => a.status === ActivityStatus.IN_PROGRESS
  ).length;
  const totalPoints = currentWeekActivities
    .filter((a) =>
      [ActivityStatus.COMPLETED, ActivityStatus.VERIFIED].includes(a.status)
    )
    .reduce((sum, a) => sum + a.points, 0);

  const completionRate =
    totalActivities > 0
      ? Math.round((completedActivities / totalActivities) * 100)
      : 0;

  const leaderboardData = getLeaderboardData();
  const upcomingActivities = getUpcomingActivities();
  const weeklyProgressData = getWeeklyProgressData();
  const memberColors = getMemberColors();

  return (
    <Box sx={{ width: "100%", maxWidth: "none" }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {user ? `Welcome, ${user?.firstName}!` : "Dashboard"}
      </Typography>

      <Grid container spacing={3}>
        {/* Weekly Progress Chart */}
        <Grid item xs={12} xl={isAdmin ? 9 : 12}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              background: "linear-gradient(135deg, #174B67 0%, #2BB3A3 100%)",
              color: "white",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3, color: "white" }}
            >
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Weekly Progress & Upcoming Activities
            </Typography>

            {weeklyProgressData.length === 0 || familyMembers.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 8 }}
              >
                No activity data available for this week
              </Typography>
            ) : (
              <Box sx={{ width: "100%", height: 400 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={weeklyProgressData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255,255,255,0.3)"
                    />
                    <XAxis
                      dataKey="dayWithDate"
                      stroke="rgba(255,255,255,0.8)"
                      fontSize={12}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="rgba(255,255,255,0.8)"
                      fontSize={12}
                      tickLine={false}
                      label={{
                        value: "Points Earned",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          textAnchor: "middle",
                          fill: "rgba(255,255,255,0.8)",
                        },
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      }}
                      formatter={(value: any, name: string) => [
                        `${value} points`,
                        name,
                      ]}
                      labelFormatter={(label: string) => `Day: ${label}`}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: "20px",
                      }}
                    />

                    {/* Line for each family member - Combined Progress */}
                    {familyMembers.map((member) => (
                      <Line
                        key={member.userId}
                        type="monotone"
                        dataKey={member.firstName}
                        stroke={memberColors[member.firstName]}
                        strokeWidth={3}
                        dot={{
                          fill: memberColors[member.firstName],
                          strokeWidth: 2,
                          r: 6,
                        }}
                        activeDot={{
                          r: 8,
                          stroke: memberColors[member.firstName],
                          strokeWidth: 2,
                          fill: "#fff",
                        }}
                        name={member.firstName}
                      />
                    ))}

                    {/* Family total line - Combined Progress */}
                    <Line
                      type="monotone"
                      dataKey="familyTotal"
                      stroke="#FFD700"
                      strokeWidth={3}
                      strokeDasharray="5 5"
                      dot={{ fill: "#FFD700", strokeWidth: 2, r: 5 }}
                      activeDot={{
                        r: 7,
                        stroke: "#FFD700",
                        strokeWidth: 2,
                        fill: "#fff",
                      }}
                      name="Family Total"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Typography
              variant="caption"
              sx={{
                mt: 2,
                display: "block",
                textAlign: "center",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              Week starts on{" "}
              {
                [
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ][familyData?.weekStartDay ?? 1]
              }{" "}
              • Lines show completed points up to today, then projected upcoming
              points
            </Typography>
          </Paper>
        </Grid>

        {/* Weekly Leaderboard Chart */}
        {isAdmin && (
          <Grid item xs={12} xl={3}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: "bold", mb: 3 }}
              >
                <TrophyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Weekly Leaderboard
              </Typography>

              <Stack spacing={2}>
                {leaderboardData.map((entry, index) => {
                  const member = familyMembers.find(
                    (m) => m.userId === entry.userId
                  );
                  return (
                    <Box
                      key={entry.userId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        borderRadius: 2,
                        backgroundColor:
                          index === 0
                            ? "rgba(255, 215, 0, 0.1)"
                            : index === 1
                            ? "rgba(192, 192, 192, 0.1)"
                            : index === 2
                            ? "rgba(205, 127, 50, 0.1)"
                            : "transparent",
                        border: index < 3 ? "2px solid" : "1px solid",
                        borderColor:
                          index === 0
                            ? "warning.main"
                            : index === 1
                            ? "grey.400"
                            : index === 2
                            ? "orange"
                            : "divider",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", flex: 1 }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            minWidth: 32,
                            textAlign: "center",
                            fontWeight: "bold",
                            color:
                              index === 0 ? "warning.main" : "text.primary",
                          }}
                        >
                          {index === 0
                            ? "🥇"
                            : index === 1
                            ? "🥈"
                            : index === 2
                            ? "🥉"
                            : `#${index + 1}`}
                        </Typography>

                        {member && (
                          <Box sx={{ mx: 2 }}>
                            <UserAvatar user={member} size={40} />
                          </Box>
                        )}

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {entry.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {entry.completedThisWeek} completed this week
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary"
                          >
                            {entry.points} pts
                          </Typography>
                          {entry.money > 0 && (
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="success.main"
                            >
                              ${entry.money.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

                {leaderboardData.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    No family members found
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Activity Statistics */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              Weekly Activity Statistics
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {totalActivities}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="error.main" fontWeight="bold">
                    {overdueActivities.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main" fontWeight="bold">
                    {inProgressActivities}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Progress
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography
                    variant="h3"
                    color="success.main"
                    fontWeight="bold"
                  >
                    {completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Points Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              Weekly Points Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography
                    variant="h3"
                    color="success.main"
                    fontWeight="bold"
                  >
                    {totalPoints}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Earned
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h3" color="error.main" fontWeight="bold">
                    {overdueActivities.reduce((sum, a) => sum + a.points, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Points Overdue
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Upcoming Activities */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              <CalendarIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Upcoming Activities
            </Typography>

            {upcomingActivities.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No upcoming activities in the next 7 days
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {upcomingActivities.map((activity, index) => (
                  <ListItem
                    key={activity.activityId}
                    sx={{
                      px: 0,
                      borderBottom:
                        index < upcomingActivities.length - 1
                          ? "1px solid"
                          : "none",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: getCategoryColor(activity.category),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getCategoryIcon(activity.category)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body1" fontWeight="medium">
                            {activity.title}
                          </Typography>
                          <Chip
                            label={`${activity.points} pts`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <PersonIcon
                              fontSize="small"
                              sx={{ mr: 0.5, verticalAlign: "middle" }}
                            />
                            {getMemberName(activity.userId)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Due:{" "}
                            {new Date(activity.dueDate!).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Overdue Activities */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "rgba(211, 47, 47, 0.05)",
              border: "2px solid",
              borderColor: "error.main",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "error.main",
                display: "flex",
                alignItems: "center",
              }}
            >
              <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Overdue Activities ⚠️
            </Typography>

            {overdueActivities.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 4 }}
              >
                Great! No overdue activities
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {overdueActivities
                  .sort((a, b) => {
                    // Sort by most overdue first
                    const aDaysOverdue = Math.floor(
                      (new Date().getTime() - new Date(a.dueDate!).getTime()) /
                        (1000 * 60 * 60 * 24)
                    );
                    const bDaysOverdue = Math.floor(
                      (new Date().getTime() - new Date(b.dueDate!).getTime()) /
                        (1000 * 60 * 60 * 24)
                    );
                    return bDaysOverdue - aDaysOverdue;
                  })
                  .slice(0, 6)
                  .map((activity, index) => {
                    const daysOverdue = Math.floor(
                      (new Date().getTime() -
                        new Date(activity.dueDate!).getTime()) /
                        (1000 * 60 * 60 * 24)
                    );

                    return (
                      <ListItem
                        key={activity.activityId}
                        sx={{
                          px: 0,
                          borderBottom:
                            index < Math.min(overdueActivities.length, 6) - 1
                              ? "1px solid"
                              : "none",
                          borderColor: "error.light",
                          backgroundColor:
                            index === 0
                              ? "rgba(211, 47, 47, 0.1)"
                              : "transparent",
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: "error.main",
                              width: 40,
                              height: 40,
                              border: "2px solid",
                              borderColor: "error.dark",
                            }}
                          >
                            <WarningIcon sx={{ color: "white" }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography
                                variant="body1"
                                fontWeight="medium"
                                sx={{ color: "error.dark" }}
                              >
                                {activity.title}
                              </Typography>
                              <Chip
                                label={`${daysOverdue} day${
                                  daysOverdue !== 1 ? "s" : ""
                                } overdue`}
                                size="small"
                                color="error"
                                variant="filled"
                                sx={{
                                  fontWeight: "bold",
                                  animation:
                                    daysOverdue > 3
                                      ? `${pulse} 2s infinite`
                                      : "none",
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: "error.dark" }}
                              >
                                <PersonIcon
                                  fontSize="small"
                                  sx={{ mr: 0.5, verticalAlign: "middle" }}
                                />
                                {getMemberName(activity.userId)} •{" "}
                                {activity.points} points at risk
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "error.main" }}
                              >
                                Was due:{" "}
                                {new Date(
                                  activity.dueDate!
                                ).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
              </List>
            )}

            {overdueActivities.length > 0 && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", fontWeight: "bold" }}
                >
                  Total Points at Risk:{" "}
                  {overdueActivities.reduce((sum, a) => sum + a.points, 0)}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recently Completed Activities */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3 }}
            >
              <CompletedIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Recently Completed
            </Typography>

            {recentCompletedActivities.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No completed activities yet
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {recentCompletedActivities.map((activity, index) => (
                  <ListItem
                    key={activity.activityId}
                    sx={{
                      px: 0,
                      borderBottom:
                        index < recentCompletedActivities.length - 1
                          ? "1px solid"
                          : "none",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          backgroundColor: getCategoryColor(activity.category),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getCategoryIcon(activity.category)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="body1" fontWeight="medium">
                            {activity.title}
                          </Typography>
                          <Chip
                            label={`${activity.points} pts`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <PersonIcon
                              fontSize="small"
                              sx={{ mr: 0.5, verticalAlign: "middle" }}
                            />
                            {getMemberName(activity.userId)}
                          </Typography>
                          {activity.completedDate && (
                            <Typography variant="body2" color="text.secondary">
                              Completed:{" "}
                              {new Date(
                                activity.completedDate
                              ).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
